/** @prettier */

import {
  Dialog,
  DialogTitle,
  Box,
  DialogActions,
  Stack,
  Radio,
  DialogContent,
  Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from 'dayjs';
import {utcToZonedTime} from 'date-fns-tz';
import {useParams} from 'react-router-dom';

//MTH component import
import TextFieldSelectBox from '../../component/SelectBox';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {getSettingUser, putSiteCalenderRoutine} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';

import {apiErrorAlert} from '../../functions/apiErrorAlert';
import {pullDownMenu} from '../../define';
import {decideDayjsDatePickerLocale} from '../../functions/decideDayjsDatePickerLocale';

//オブジェクトのキーを抽出した型を作成
type dayType = 'weekday' | 'holiday' | 'special-day-1' | 'special-day-2';

/**
 * カレンダー日付指定 編集ダイアログ
 * @param props props
 * @returns JSX.Element
 */
export const DesignatedDaySettingDialog = (props: {
  /** タイムゾーン */
  timeZone: string;
  /** 標準設定のデータ */
  calenderRoutineData: apiIF.res.getSiteCalenderRoutine;
  /** 日付指定編集ダイアログ 表示フラグ */
  isOpenDialog: boolean;
  /** 日付指定編集ダイアログ 表示フラグ 設定関数 */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 追加か編集か */
  editType?: 'day' | 'week';
  /** 変更する設定の保存場所 */
  tableIndex?: number;
  /** 設定変更フラグ */
  setIsRoutineChange: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID} = useParams();

  /** 日付指定 */
  const [daySetting, setDaySetting] = useState('01-01');
  /** 今年の年を取得 */
  const year = utcToZonedTime(new Date(), props.timeZone).getFullYear();
  //prettier-ignore
  /** datePickerで選択した日付 */
  const [datePickerValue, setDatePickerValue] = useState<Dayjs>(dayjs(`${year}/01/01`));
  //prettier-ignore
  /** 週、曜日、月、指定の週設定（第n週）*/
  const [weekOfWeekSetting, setWeekOfWeekSetting] = useState<apiIF.resParameters.specifiedWeek['week-number']>('first');
  //prettier-ignore
  /** 週、曜日、月、指定の曜日設定 */
  const [dayOfWeekSetting, setDayOfWeekSetting] = useState<apiIF.resParameters.specifiedWeek['day-of-week']>('monday');
  //prettier-ignore
  /** 週、曜日、月、指定の月設定 */
  const [monthOfWeekSetting, setMonthOfWeekSetting] = useState<apiIF.resParameters.specifiedWeek['month']>('january');
  /** チェックボックス用 */
  const [checked, setChecked] = useState([true, false]);
  /** 日タイプ設定 */
  const [dayTypeSetting, setDayTypeSetting] = useState<dayType>('weekday');
  /** 右下、決定ボタンのテキスト */
  const [okButtonText, setOkButtonText] = useState<'add' | 'ok'>('add');
  //prettier-ignore
  /** 言語情報 */
  const [language, setLanguage] = useState<apiIF.res.getSettingUser['language']>('english');
  /** API応答待ちフラグ */
  const [apiWait, setAPIWait] = useState(false);

  /**
   * ダイアログオープン時の初期設定
   */
  useEffect(() => {
    //ダイアログクローズの場合は何もしない
    if (props.isOpenDialog === false) {
      return;
    }

    if (props.tableIndex === undefined) {
      //追加する場合
      setChecked([true, false]);
      setDaySetting('01-01');
      setDatePickerValue(dayjs(`${year}/01/01`));
      setWeekOfWeekSetting('first');
      setDayOfWeekSetting('monday');
      setMonthOfWeekSetting('january');
      setDayTypeSetting('weekday');
      setOkButtonText('add');
    } else {
      //編集する場合
      switch (props.editType) {
        case 'day':
          const tempSpecifiedDate = props.calenderRoutineData['specified-day'][
            props.tableIndex
          ] as apiIF.resParameters.specifiedDate;
          const tempDay = tempSpecifiedDate['date'].substring(3, 5);
          const tempMonthNum = tempSpecifiedDate['date'].substring(0, 2);
          //ラジオボタンを日に設定
          setChecked([true, false]);
          //DBから値設定
          setDaySetting(`${tempMonthNum}-${tempDay}`);
          setDatePickerValue(dayjs(`${year}/${tempMonthNum}/${tempDay}`));
          //週、曜日、月、指定の値は初期値
          setWeekOfWeekSetting('first');
          setDayOfWeekSetting('monday');
          setMonthOfWeekSetting('january');
          //DBから日タイプを設定
          setDayTypeSetting(
            props.calenderRoutineData['specified-day'][props.tableIndex][
              'schedule'
            ]
          );
          break;
        case 'week':
          const tempSpecifiedWeek = props.calenderRoutineData['specified-day'][
            props.tableIndex
          ] as apiIF.resParameters.specifiedWeek;
          //ラジオボタンを週に設定
          setChecked([false, true]);
          //日指定の値は初期値
          setDaySetting('01-01');
          setDatePickerValue(dayjs(`${year}/01/01`));
          //DBから週の値設定
          setWeekOfWeekSetting(tempSpecifiedWeek['week-number']);
          //DBから曜日の値設定
          setDayOfWeekSetting(tempSpecifiedWeek['day-of-week']);
          //DBから月の値設定
          setMonthOfWeekSetting(tempSpecifiedWeek['month']);
          //DBから日タイプを設定
          setDayTypeSetting(
            props.calenderRoutineData['specified-day'][props.tableIndex][
              'schedule'
            ]
          );
          break;
      }
      setOkButtonText('ok');
    }
  }, [props.isOpenDialog]);

  //言語切り替え時、言語情報更新
  useEffect(() => {
    getSettingUser().then(response => {
      setLanguage(response.language);
    });
  }, [trans]);

  /**
   * 指定日(日)を追加、または編集する処理
   *  @param specifiedDate 特定日
   */
  const editSpecifiedDateSetting = (
    specifiedDate: apiIF.resParameters.specifiedDate
  ) => {
    const isModify = props.calenderRoutineData['specified-day'].some(value => {
      return 'date' in value && value.date === specifiedDate.date;
    });

    //同じ日付がある場合、設定値を上書き
    if (isModify) {
      return props.calenderRoutineData['specified-day'].map(value => {
        if ('date' in value && value.date === specifiedDate.date) {
          return {...value, schedule: specifiedDate.schedule};
        }
        return value;
      });
    }

    const newSpecifiedDay = JSON.parse(
      JSON.stringify(props.calenderRoutineData['specified-day'])
    ) as apiIF.res.getSiteCalenderRoutine['specified-day'];
    if (props.tableIndex === undefined) {
      //追加する場合
      newSpecifiedDay.push(specifiedDate);
    } else {
      //編集する場合
      newSpecifiedDay[props.tableIndex] = specifiedDate;
    }
    return newSpecifiedDay;
  };

  /**
   * 指定日(週)を追加、または編集する処理
   *  @param specifiedWeek 特定日
   */
  const editSpecifiedWeekSetting = (
    specifiedWeek: apiIF.resParameters.specifiedWeek
  ) => {
    const isModify = props.calenderRoutineData['specified-day'].some(value => {
      return (
        'month' in value &&
        value.month === specifiedWeek.month &&
        'week-number' in value &&
        value['week-number'] === specifiedWeek['week-number'] &&
        'day-of-week' in value &&
        value['day-of-week'] === specifiedWeek['day-of-week']
      );
    });

    //同じ曜日がある場合、設定値を上書き
    if (isModify) {
      return props.calenderRoutineData['specified-day'].map(value => {
        if (
          'month' in value &&
          value.month === specifiedWeek.month &&
          'week-number' in value &&
          value['week-number'] === specifiedWeek['week-number'] &&
          'day-of-week' in value &&
          value['day-of-week'] === specifiedWeek['day-of-week']
        ) {
          return {...value, schedule: specifiedWeek.schedule};
        }
        return value;
      });
    }

    const newSpecifiedDay = JSON.parse(
      JSON.stringify(props.calenderRoutineData['specified-day'])
    ) as apiIF.res.getSiteCalenderRoutine['specified-day'];
    if (props.tableIndex === undefined) {
      //追加する場合
      newSpecifiedDay.push(specifiedWeek);
    } else {
      //編集する場合
      newSpecifiedDay[props.tableIndex] = specifiedWeek;
    }
    return newSpecifiedDay;
  };

  /**
   * 指定日（日）編集を選択時の処理
   */
  const selectDay = () => {
    setChecked([true, false]);
  };

  /**
   * 指定日（週）編集を選択時の処理
   */
  const selectWeek = () => {
    setChecked([false, true]);
  };

  /**
   * 日付選択時の処理
   * @param date カレンダーで選択した日付
   */
  const dateChange = (date: dayjs.Dayjs | null) => {
    //日付がおかしい場合
    if (date === null || date.isValid() === false) return;
    setDatePickerValue(date);
    /** DatePickerより取得した日付をAPIへ送信する形式に整える */
    const selectDate = date.startOf('day').format('MM-DD');
    setDaySetting(selectDate);
  };

  /**
   * 決定ボタンを押したときの処理 設定したデータを送る
   */
  const handleOK = () => {
    if (siteID === undefined) return;

    setAPIWait(true);
    /** 修正後の設定値 */
    const editData = JSON.parse(
      JSON.stringify(props.calenderRoutineData)
    ) as apiIF.res.getSiteCalenderRoutine;

    if (checked[0]) {
      //日付指定の場合
      const tempSpecifiedDate: apiIF.resParameters.specifiedDate = {
        date: daySetting,
        schedule: dayTypeSetting,
      };
      editData['specified-day'] = editSpecifiedDateSetting(tempSpecifiedDate);
    } else {
      //週、曜日、月指定の場合
      const tempSpecifiedWeek: apiIF.resParameters.specifiedWeek = {
        'month': monthOfWeekSetting,
        'week-number': weekOfWeekSetting,
        'day-of-week': dayOfWeekSetting,
        'schedule': dayTypeSetting,
      };
      editData['specified-day'] = editSpecifiedWeekSetting(tempSpecifiedWeek);
    }

    putSiteCalenderRoutine(siteID, editData)
      .then(response => {
        //設定更新フラグtrue
        props.setIsRoutineChange(true);
        handleCancel();
      })
      .catch(err => {
        apiErrorAlert(trans);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };

  /**
   * キャンセルボタンを押したときの処理 ダイアログを閉じる
   */
  const handleCancel = () => {
    props.setIsOpenDialog(false);
  };

  return (
    <Dialog open={props.isOpenDialog} onClose={handleCancel} maxWidth="md">
      <DialogTitle>{trans('DesignatedDaySettingDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {/** 日付指定 */}
          <Box>
            <Stack>
              <Box className="schedule__designatedDaySettingDialog__date__title">
                {trans('DesignatedDaySettingDialog.subTitle.targetDate')}
              </Box>
              <FormControl>
                <RadioGroup
                  aria-labelledby="radio-button-dayOrWeek-label"
                  className="schedule__designatedDaySettingDialog__date__radioGroup">
                  <Stack spacing={2}>
                    {/** 日時設定 */}
                    <Box>
                      <FormControlLabel
                        checked={checked[0]}
                        control={<Radio size="small" onChange={selectDay} />}
                        label={
                          <Box component="span">
                            {trans('DesignatedDaySettingDialog.radioMenu.day')}
                          </Box>
                        }
                      />
                      <Box className="schedule__designatedDaySettingDialog__date__radioGroup__setting">
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale={decideDayjsDatePickerLocale(language)}>
                          <DatePicker
                            views={['month', 'day']}
                            format="DD/MM"
                            value={datePickerValue}
                            onChange={date => dateChange(date)}
                            minDate={dayjs(`${year}/01/01`)}
                            maxDate={dayjs(`${year}/12/31`)}
                            disabled={checked[1]}
                            slotProps={{
                              textField: {size: 'small'},
                            }}
                            className="schedule__designatedDaySettingDialog__date__radioGroup__setting__input--calender"
                          />
                        </LocalizationProvider>
                      </Box>
                    </Box>
                    {/** 週設定 */}
                    <Box>
                      <FormControlLabel
                        checked={checked[1]}
                        control={<Radio size="small" onChange={selectWeek} />}
                        label={
                          <Box component="span">
                            {trans('DesignatedDaySettingDialog.radioMenu.week')}
                          </Box>
                        }
                      />
                      <Grid
                        container
                        columnSpacing={4}
                        className="schedule__designatedDaySettingDialog__date__radioGroup__setting">
                        <Grid
                          item
                          xs="auto"
                          container
                          alignItems="flex-end"
                          columnSpacing={1}>
                          <Grid item xs="auto">
                            <TextFieldSelectBox
                              choices={pullDownMenu.WEEK_OF_WEEK_SETTING}
                              value={weekOfWeekSetting}
                              setValue={setWeekOfWeekSetting}
                              disabled={checked[0]}
                              margin="none"
                              class="schedule__designatedDaySettingDialog__date__radioGroup__setting__input--weekNumber"
                            />
                          </Grid>
                          <Grid item xs>
                            {trans(
                              'DesignatedDaySettingDialog.specifiedWeek.week'
                            )}
                          </Grid>
                        </Grid>
                        <Grid
                          item
                          xs="auto"
                          container
                          alignItems="flex-end"
                          columnSpacing={1}>
                          <Grid item xs="auto">
                            <TextFieldSelectBox
                              choices={pullDownMenu.DAY_OF_WEEK_SETTING}
                              value={dayOfWeekSetting}
                              setValue={setDayOfWeekSetting}
                              disabled={checked[0]}
                              margin="none"
                              class="schedule__designatedDaySettingDialog__date__radioGroup__setting__input--dayOfWeek"
                            />
                          </Grid>
                          <Grid item xs="auto">
                            {trans(
                              'DesignatedDaySettingDialog.specifiedWeek.of'
                            )}
                          </Grid>
                          <Grid item xs>
                            <TextFieldSelectBox
                              choices={pullDownMenu.MONTH_OF_WEEK_SETTING}
                              value={monthOfWeekSetting}
                              setValue={setMonthOfWeekSetting}
                              disabled={checked[0]}
                              margin="none"
                              class="schedule__designatedDaySettingDialog__date__radioGroup__setting__input--month"
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                </RadioGroup>
              </FormControl>
            </Stack>
          </Box>
          {/** 特定日指定 */}
          <Box>
            <Stack>
              <Box className="schedule__designatedDaySettingDialog__date__title">
                {trans('DesignatedDaySettingDialog.subTitle.dataType')}
              </Box>
              <Box className="schedule__designatedDaySettingDialog__template__setting">
                <TextFieldSelectBox
                  choices={pullDownMenu.DAY_TYPE_SETTING}
                  value={dayTypeSetting}
                  setValue={setDayTypeSetting}
                  margin="normal"
                  class="schedule__designatedDaySettingDialog__template__setting__selectBox"
                />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        {/** CANCEL button */}
        <SecondaryButton
          onClick={handleCancel}
          buttonText={trans('DesignatedDaySettingDialog.buttonTxt.cancel')}
          disabled={apiWait}
        />
        {/** OK button */}
        <PrimaryButton
          onClick={handleOK}
          buttonText={trans(
            `DesignatedDaySettingDialog.buttonTxt.${okButtonText}`
          )}
          disabled={apiWait}
        />
      </DialogActions>
    </Dialog>
  );
};
